import Icon, { BarChartOutlined, PushpinOutlined, TagOutlined } from "@ant-design/icons";
import BoundingBoxIcon from "@images/icons/icon-bounding-box.svg?react";
import IconCell from "@images/icons/icon-cell.svg?react";
import RulerIcon from "@images/icons/icon-ruler.svg?react";
import { Space } from "antd";
import type { ItemType } from "antd/es/menu/interface";
import FastTooltip from "components/fast_tooltip";
import { formatLengthAsVx, formatNumberToLength } from "libs/format_utils";
import { V3 } from "libs/mjs";
import { useWkSelector } from "libs/react_hooks";
import { truncateStringToLength } from "libs/utils";
import { useState } from "react";
import { shallowEqual } from "react-redux";
import { LongUnitToShortUnitMap } from "viewer/constants";
import { getVisibleSegmentationLayer } from "viewer/model/accessors/dataset_accessor";
import {
  getActiveNode,
  getNodePosition,
  getTreeAndNode,
} from "viewer/model/accessors/skeletontracing_accessor";
import { getSegmentsForLayer } from "viewer/model/accessors/volumetracing_accessor";
import type { MutableNode, Tree } from "viewer/model/types/tree_types";
import { api } from "viewer/singletons";
import type { ContextMenuInfo } from "viewer/store";
import { CopyIconWithTooltip } from "./copy_icon_with_tooltip";
import { getInfoMenuItem, positionToString } from "./helpers";
import { useSegmentStatisticsLabels } from "./use_segment_statistics_labels";

const NO_SEGMENT_ID = 0n;

export function useContextMenuInfoRows(contextInfo: ContextMenuInfo, segmentIdAtPosition: bigint) {
  const {
    globalPosition,
    contextMenuPosition,
    meshId: maybeClickedMeshId,
    clickedNodeId: maybeClickedNodeId,
  } = contextInfo;

  const [segmentStatsTriggerDate, setSegmentStatsTriggerDate] = useState<Date | null>(null);

  const handleRefreshSegmentStatistics = async () => {
    await api.tracing.save();
    setSegmentStatsTriggerDate(new Date());
  };

  // Currently either segmentIdAtPosition or maybeClickedMeshId is set, but not both.
  // segmentIdAtPosition is only set if a segment is hovered in one of the xy, xz, or yz viewports.
  // maybeClickedMeshId is only set, when a mesh is hovered in the 3d viewport.
  // Thus the segment id is always unambiguous / clearly defined.
  const clickedSegmentOrMeshId =
    maybeClickedMeshId != null ? maybeClickedMeshId : segmentIdAtPosition;
  const wasSegmentOrMeshClicked = clickedSegmentOrMeshId !== NO_SEGMENT_ID;

  const skeletonTracing = useWkSelector((state) => state.annotation.skeleton);
  const voxelSize = useWkSelector((state) => state.dataset.dataSource.scale);
  const activeTreeId = useWkSelector((state) => state.localSkeletonState.activeTreeId);
  const additionalCoordinates = useWkSelector(
    (state) => state.flycam.additionalCoordinates || undefined,
  );
  const visibleSegmentationLayer = useWkSelector(getVisibleSegmentationLayer);
  const segments = useWkSelector((state) =>
    visibleSegmentationLayer != null
      ? getSegmentsForLayer(state, visibleSegmentationLayer.name)
      : null,
  );

  const {
    segmentVolumeLabel,
    boundingBoxInfoLabel,
    segmentSurfaceAreaLabel,
    areSegmentStatisticsAvailable,
    isBoundingBoxAvailable,
    isVolumeAvailable,
    isSurfaceAreaAvailable,
  } = useSegmentStatisticsLabels(
    clickedSegmentOrMeshId,
    segmentStatsTriggerDate,
    contextMenuPosition,
    wasSegmentOrMeshClicked,
  );

  let nodeContextMenuTree: Tree | null = null;
  let nodeContextMenuNode: MutableNode | null = null;

  if (skeletonTracing != null && maybeClickedNodeId != null) {
    const treeAndNode = getTreeAndNode(skeletonTracing, activeTreeId, maybeClickedNodeId);
    if (treeAndNode) {
      nodeContextMenuTree = treeAndNode[0];
      nodeContextMenuNode = treeAndNode[1];
    }
  }

  // Node positions depend on the skeleton transforms, which are read from the state.
  const clickedNodesPosition = useWkSelector(
    (state) => (nodeContextMenuNode != null ? getNodePosition(nodeContextMenuNode, state) : null),
    shallowEqual,
  );

  const positionToMeasureDistanceTo =
    nodeContextMenuNode != null ? clickedNodesPosition : globalPosition;
  const activeNode = skeletonTracing != null ? getActiveNode(skeletonTracing, activeTreeId) : null;
  const activeNodePosition = useWkSelector(
    (state) => (activeNode != null ? getNodePosition(activeNode, state) : null),
    shallowEqual,
  );

  const distanceToSelection =
    activeNodePosition != null && positionToMeasureDistanceTo != null
      ? [
          formatNumberToLength(
            V3.scaledDist(activeNodePosition, positionToMeasureDistanceTo, voxelSize.factor),
            LongUnitToShortUnitMap[voxelSize.unit],
          ),
          formatLengthAsVx(V3.length(V3.sub(activeNodePosition, positionToMeasureDistanceTo))),
        ]
      : null;

  const nodePositionAsString =
    nodeContextMenuNode != null && clickedNodesPosition != null
      ? positionToString(clickedNodesPosition, nodeContextMenuNode.additionalCoordinates)
      : "";

  const infoRows: ItemType[] = [];

  const isSegmentActionAndAreStatisticsAvailable =
    wasSegmentOrMeshClicked && areSegmentStatisticsAvailable;
  if (isSegmentActionAndAreStatisticsAvailable) {
    infoRows.push({
      key: "load-stats",
      icon: <BarChartOutlined />,
      label: `${segmentStatsTriggerDate != null ? "Reload" : "Load"} segment statistics`,
      onClick: (event: any) => {
        event.domEvent.preventDefault();
        handleRefreshSegmentStatistics();
      },
    });
  }

  if (maybeClickedNodeId != null && nodeContextMenuTree != null) {
    infoRows.push(
      getInfoMenuItem(
        "nodeInfo",
        `Node with Id ${maybeClickedNodeId} in Tree ${nodeContextMenuTree.treeId}`,
      ),
    );
  }

  if (nodeContextMenuNode != null) {
    infoRows.push(
      getInfoMenuItem(
        "positionInfo",
        <Space size="small">
          <PushpinOutlined rotate={-45} />
          {`Position: ${nodePositionAsString}`}
          <CopyIconWithTooltip value={nodePositionAsString} label="node position" />
        </Space>,
      ),
    );
  } else if (globalPosition != null) {
    const positionAsString = positionToString(globalPosition, additionalCoordinates);
    infoRows.push(
      getInfoMenuItem(
        "positionInfo",
        <Space size="small">
          <PushpinOutlined rotate={-45} />
          {`Position: ${positionAsString}`}
          <CopyIconWithTooltip value={positionAsString} label="position" />
        </Space>,
      ),
    );
  }

  if (distanceToSelection != null) {
    infoRows.push(
      getInfoMenuItem(
        "distanceInfo",
        <Space size="small">
          <Icon component={RulerIcon} />
          <FastTooltip title="Distance to the active Node of the active Tree">
            {`${distanceToSelection[0]} (${distanceToSelection[1]}) to this
            ${maybeClickedNodeId != null ? "Node" : "Position"}`}
          </FastTooltip>
          <CopyIconWithTooltip value={distanceToSelection[0]} label="distance" />
        </Space>,
      ),
    );
  }

  if (wasSegmentOrMeshClicked) {
    infoRows.push(
      getInfoMenuItem(
        "copy-segment-id",
        <Space size="small">
          <Icon component={IconCell} />
          {`Segment ID: ${clickedSegmentOrMeshId}`}
          <CopyIconWithTooltip value={clickedSegmentOrMeshId.toString()} label="segment ID" />
        </Space>,
      ),
    );
  }

  if (segments != null && wasSegmentOrMeshClicked) {
    const segmentName = segments.getNullable(clickedSegmentOrMeshId)?.name;
    if (segmentName != null) {
      const maxNameLength = 20;
      const segmentNameLabel =
        segmentName.length > maxNameLength
          ? truncateStringToLength(segmentName, maxNameLength)
          : segmentName;
      infoRows.push(
        getInfoMenuItem(
          "copy-segment-name",
          <Space size="small">
            <TagOutlined />
            {`Segment Name: ${segmentNameLabel}`}
            <CopyIconWithTooltip value={segmentName} label="segment name" />
          </Space>,
        ),
      );
    }
  }

  if (isSegmentActionAndAreStatisticsAvailable && segmentStatsTriggerDate != null) {
    // Each statistic is only shown when this layer can actually answer it — otherwise the row would
    // be permanently stuck on an error message.
    if (isSurfaceAreaAvailable) {
      infoRows.push(
        getInfoMenuItem(
          "surfaceInfo",
          <Space size="small">
            <i>m²</i>
            {`Surface Area: ${segmentSurfaceAreaLabel}`}
            <CopyIconWithTooltip value={segmentSurfaceAreaLabel} label="surface area" />
          </Space>,
        ),
      );
    }

    if (isVolumeAvailable) {
      infoRows.push(
        getInfoMenuItem(
          "volumeInfo",
          <Space size="small">
            <i>m³</i>
            {`Volume: ${segmentVolumeLabel}`}
            <CopyIconWithTooltip value={segmentVolumeLabel} label="segment volume" />
          </Space>,
        ),
      );
    }

    if (isBoundingBoxAvailable) {
      infoRows.push(
        getInfoMenuItem(
          "boundingBoxPositionInfo",
          <Space size="small">
            <Icon component={BoundingBoxIcon} />
            {`Bounding Box: ${boundingBoxInfoLabel}`}
            <CopyIconWithTooltip
              value={boundingBoxInfoLabel}
              label="Bbox top left point and extent"
            />
          </Space>,
        ),
      );
    }
  }

  if (infoRows.length > 0) {
    infoRows.unshift({
      key: "divider",
      type: "divider",
      className: "hide-if-first hide-if-last",
      style: {
        margin: "4px 0px",
      },
    });
  }

  return {
    infoRows,
  };
}
